import { useMemo } from 'react';
import {
  Modal,
  ModalSizes,
  Table,
  TableCellTypes,
  ButtonVariants,
  Typography,
  TypographyVariants,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { buildTableLabels } from '@/config/tableLabels';
import { EMPTY_LIST, downloadAttachment } from '@/lib/utils';
import { useGetTransactionAttachmentsQuery } from '../api/transactionsApi';

const getAttachmentId = (row) => row.id;

/**
 * Props:
 *   open           whether the modal is shown
 *   onClose        ✕ / Close
 *   module         the active module route segment
 *   transaction    the row whose attachments these are
 */
export const TransactionAttachmentsModal = ({ open, onClose, module, transaction }) => {
  const t = useTranslations();

  const { data: attachments = EMPTY_LIST, isFetching } = useGetTransactionAttachmentsQuery(
    { module, id: transaction?.id },
    { skip: !open || !transaction?.id },
  );

  const columns = useMemo(
    () => [
      {
        id: 'serialNo',
        header: t('TRANSACTIONS_COL_SERIAL_NO'),
        type: TableCellTypes.text,
        width: 80,
      },
      { id: 'name', header: t('FIELD_FILE_NAME'), type: TableCellTypes.text, width: 320 },
      {
        id: 'attachmentType',
        header: t('TRANSACTIONS_ATTACHMENT_TYPE'),
        type: TableCellTypes.text,
        width: 200,
      },
    ],
    [t],
  );

  const buttons = useMemo(
    () => [{ label: t('COMMON_CLOSE'), variant: ButtonVariants.neutral, onClick: onClose }],
    [t, onClose],
  );

  // Read-only by design — it lists the batch's files too, which this transaction can't remove.
  const rowActions = useMemo(
    () => [
      {
        id: 'download',
        label: t('ATTACHMENT_ACTION_DOWNLOAD'),
        variant: ButtonVariants.tertiary,
        onClick: downloadAttachment,
      },
    ],
    [t],
  );

  const emptyState = useMemo(() => ({ title: t('TRANSACTIONS_ATTACHMENTS_EMPTY') }), [t]);

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const title = useMemo(
    () => t('TRANSACTIONS_ATTACHMENTS_TITLE', { transactionId: transaction?.transactionId ?? '' }),
    [t, transaction],
  );

  return (
    <Modal open={open} onClose={onClose} size={ModalSizes.large} title={title} buttons={buttons}>
      <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
        {t('TRANSACTIONS_ATTACHMENTS_NOTE')}
      </Typography>
      <Table
        columns={columns}
        data={attachments}
        total={attachments.length}
        loading={isFetching}
        getRowId={getAttachmentId}
        showPagination={false}
        emptyState={emptyState}
        labels={tableLabels}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
      />
    </Modal>
  );
};
